import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Target, Award, Heart, ArrowRight, CheckCircle, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { trackPageView } from '../lib/analytics';

const About = () => {
  useEffect(() => { trackPageView('about'); }, []);
  return (
    <div className="page-about-new">
      {/* Hero Section */}
      <section className="about-hero-new">
        <div className="about-hero-bg">
          <img 
            src="https://images.unsplash.com/photo-1645223584144-fcff071bae63?crop=entropy&cs=srgb&fm=jpg&q=85" 
            alt="" 
            className="about-hero-img"
          />
          <div className="about-hero-overlay"></div>
        </div>
        <div className="about-hero-content">
          <span className="hero-label">About Us</span>
          <h1 className="about-headline">RAISING THE<br />STANDARD</h1>
          <p className="about-subline">
            We're not just another directory. We're building the UK's most trusted 
            network of Gas Safe professionals.
          </p>
        </div>
      </section>

      {/* Mission - Visual Statement */}
      <section className="mission-section-new">
        <div className="container-premium">
          <div className="mission-grid">
            <div className="mission-statement">
              <span className="section-label">Our Mission</span>
              <h2 className="mission-headline">
                "Gas work is <span className="text-highlight">safety-critical</span>. 
                Homeowners deserve better than overcrowded directories 
                and untested listings."
              </h2>
            </div>
            <div className="mission-points">
              <div className="mission-point">
                <CheckCircle size={24} />
                <span>Every engineer thoroughly vetted</span>
              </div>
              <div className="mission-point">
                <CheckCircle size={24} />
                <span>100% free for engineers — no subscriptions</span>
              </div>
              <div className="mission-point">
                <CheckCircle size={24} />
                <span>No lead selling or bidding wars</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values - Icon Grid */}
      <section className="values-section-new">
        <div className="container-premium">
          <div className="section-header-premium">
            <span className="section-label">What Drives Us</span>
            <h2 className="section-title-premium">OUR VALUES</h2>
          </div>
          
          <div className="values-grid-new">
            <div className="value-card-new">
              <div className="value-icon-wrap">
                <Shield size={32} />
              </div>
              <h3>Safety First</h3>
              <p>Gas Safe verified. Insurance checked. No exceptions.</p>
            </div>
            
            <div className="value-card-new">
              <div className="value-icon-wrap">
                <Award size={32} />
              </div>
              <h3>Built for Engineers</h3>
              <p>100% free to join. No subscriptions. Just exposure.</p>
            </div>
            
            <div className="value-card-new">
              <div className="value-icon-wrap">
                <Target size={32} />
              </div>
              <h3>Transparency</h3>
              <p>No hidden fees. No shared leads. Clear and honest.</p>
            </div>
            
            <div className="value-card-new">
              <div className="value-icon-wrap">
                <Heart size={32} />
              </div>
              <h3>Trust & Integrity</h3>
              <p>Ongoing monitoring. Standards that don't slip.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison - Visual */}
      <section className="comparison-section-new">
        <div className="container-premium">
          <div className="section-header-premium">
            <span className="section-label">The Difference</span>
            <h2 className="section-title-premium">WHY WE'RE DIFFERENT</h2>
          </div>
          
          <div className="comparison-cards">
            <div className="comparison-card negative">
              <h3>Other Directories</h3>
              <ul>
                <li><X size={18} /> Anyone can pay to list</li>
                <li><X size={18} /> Shared leads & bidding wars</li>
                <li><X size={18} /> £200–£400+/month subscriptions</li>
                <li><X size={18} /> Minimal vetting</li>
                <li><X size={18} /> One-time checks only</li>
              </ul>
            </div>
            
            <div className="comparison-card positive">
              <div className="recommended-badge">The Gas Guide</div>
              <h3>Our Approach</h3>
              <ul>
                <li><CheckCircle size={18} /> Rigorous Gas Safe vetting</li>
                <li><CheckCircle size={18} /> Direct contact, exclusive leads</li>
                <li><CheckCircle size={18} /> 100% free for engineers</li>
                <li><CheckCircle size={18} /> Comprehensive checks</li>
                <li><CheckCircle size={18} /> Ongoing monitoring</li>
                <li><CheckCircle size={18} /> Fair review-based ranking</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Free to join */}
      <section className="founding-section-new">
        <div className="container-premium">
          <div className="founding-card-new">
            <div className="founding-content">
              <span className="founding-badge">Free to join</span>
              <h2>BUILT FOR<br />GAS ENGINEERS</h2>
              <p>
                Every verified engineer who joins The Gas Guide gets the Founding Member badge —
                a permanent mark recognising the engineers helping build the UK&apos;s biggest
                gas-safe directory from day one.
              </p>
              <Link to="/for-engineers">
                <Button className="btn-founding">
                  Join The Gas Guide
                  <ArrowRight size={18} />
                </Button>
              </Link>
            </div>
            <div className="founding-perks">
              <div className="perk-item">
                <CheckCircle size={20} />
                <span>100% free to join</span>
              </div>
              <div className="perk-item">
                <CheckCircle size={20} />
                <span>Founding Member badge</span>
              </div>
              <div className="perk-item">
                <CheckCircle size={20} />
                <span>Direct customer enquiries</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="about-cta-new">
        <div className="container-premium">
          <div className="cta-content-new">
            <h2>JOIN US IN RAISING STANDARDS</h2>
            <p>
              Whether you're a homeowner or a professional engineer, 
              we'd love to have you.
            </p>
            <div className="cta-buttons">
              <Link to="/find-engineer">
                <Button className="btn-cta-primary">Find an Engineer</Button>
              </Link>
              <Link to="/for-engineers">
                <Button className="btn-cta-secondary">Apply as Engineer</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
